import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const ReviewsTab = ({ providerId, providerName }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingFilter, setRatingFilter] = useState('all');

  useEffect(() => {
    if (providerId) {
      fetchReviews();
    }
  }, [providerId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        ?.from('provider_reviews')
        ?.select('*')
        ?.eq('provider_id', providerId)
        ?.eq('status', 'responded')
        ?.order('review_date', { ascending: false });

      if (fetchError) throw fetchError;
      setReviews(data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const calculateAverageRating = () => {
    if (reviews?.length === 0) return 0;
    const sum = reviews?.reduce((acc, review) => acc + (review?.rating || 0), 0);
    return (sum / reviews?.length)?.toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews?.forEach(review => {
      if (review?.rating >= 1 && review?.rating <= 5) {
        distribution[review?.rating]++;
      }
    });
    return distribution;
  };

  const filteredReviews = ratingFilter === 'all'
    ? reviews
    : reviews?.filter(r => r?.rating === parseInt(ratingFilter));

  const featuredReviews = reviews?.filter(r => r?.is_featured);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Reviews laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
        <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-2" />
        <p className="text-error">{error}</p>
      </div>
    );
  }

  if (reviews?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Star" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-bold text-foreground mb-2">Nog geen beoordelingen</h3>
        <p className="text-muted-foreground">Deze aanbieder heeft nog geen klantbeoordelingen ontvangen.</p>
      </div>
    );
  }

  const averageRating = calculateAverageRating();
  const ratingDistribution = getRatingDistribution();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Rating Overview */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Average Rating */}
          <div className="text-center lg:text-left">
            <h3 className="text-lg font-bold text-foreground mb-4">Gemiddelde beoordeling</h3>
            <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-4 lg:space-y-0 lg:space-x-6">
              <div>
                <div className="text-5xl font-bold text-foreground mb-2">{averageRating}</div>
                {renderStars(Math.round(averageRating))}
                <p className="text-sm text-muted-foreground mt-2">{reviews?.length} beoordelingen</p>
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">Verdeling</h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1]?.map((rating) => {
                const count = ratingDistribution?.[rating];
                const percentage = reviews?.length > 0 ? (count / reviews?.length) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center space-x-3">
                    <span className="text-sm text-foreground w-8">{rating} ★</span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Rating Filter */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setRatingFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              ratingFilter === 'all' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            Alle beoordelingen
          </button>
          {[5, 4, 3, 2, 1]?.map((rating) => (
            <button
              key={rating}
              onClick={() => setRatingFilter(rating?.toString())}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                ratingFilter === rating?.toString()
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {rating} ★
            </button>
          ))}
        </div>
      </div>
      {/* Featured Reviews */}
      {featuredReviews?.length > 0 && ratingFilter === 'all' && (
        <div>
          <h3 className="text-xl font-bold text-foreground mb-4">Uitgelichte beoordelingen</h3>
          <div className="space-y-4">
            {featuredReviews?.map((review) => (
              <div key={review?.id} className="bg-primary/5 border-2 border-primary/20 rounded-xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <p className="font-semibold text-foreground">{review?.customer_name}</p>
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                        Uitgelicht
                      </span>
                    </div>
                    {renderStars(review?.rating)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(review?.review_date), 'd MMMM yyyy', { locale: nl })}
                  </p>
                </div>
                {review?.project_type && (
                  <p className="text-sm text-muted-foreground mb-2">Project: {review?.project_type}</p>
                )}
                <p className="text-foreground leading-relaxed mb-4">{review?.review_text}</p>
                {review?.response_text && (
                  <div className="bg-card border-l-4 border-primary pl-4 py-2">
                    <p className="text-sm font-semibold text-foreground mb-1">Reactie van {providerName}:</p>
                    <p className="text-sm text-muted-foreground">{review?.response_text}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* All Reviews */}
      <div>
        <h3 className="text-xl font-bold text-foreground mb-4">
          {ratingFilter === 'all' ? 'Alle beoordelingen' : `${ratingFilter} ster beoordelingen`}
        </h3>
        <div className="space-y-4">
          {filteredReviews?.map((review) => (
            <div key={review?.id} className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-foreground mb-2">{review?.customer_name}</p>
                  {renderStars(review?.rating)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(review?.review_date), 'd MMMM yyyy', { locale: nl })}
                </p>
              </div>
              {review?.project_type && (
                <p className="text-sm text-muted-foreground mb-2">Project: {review?.project_type}</p>
              )}
              <p className="text-foreground leading-relaxed mb-4">{review?.review_text}</p>
              {review?.response_text && (
                <div className="bg-muted border-l-4 border-primary pl-4 py-2">
                  <p className="text-sm font-semibold text-foreground mb-1">Reactie van {providerName}:</p>
                  <p className="text-sm text-muted-foreground">{review?.response_text}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ReviewsTab;